import { JSX } from 'react'
import {
  ShapesIcon,
  LinesIcon,
  HardwareSpecificsIcon,
  CircleIcon,
  TriangleIcon,
  SquareIcon,
  TorxIcon,
  PhillipsIcon,
  HexIcon,
  PozidrivIcon,
  CheckmarkIcon,
  XIcon,
  FireHazardIcon,
  WarningIcon,
  LineIcon,
  ArrowIcon,
  IconProps,
  ArrowWithBottomTextIcon,
  ArrowWithMiddleTextIcon,
  ISOElectricityIcon,
  ISOExplosiveIcon,
  ISOFlammableIcon,
  ISOObstacleIcon,
  ISOForkliftIcon,
  ISOLoudSoundsIcon,
  ISONonIonizingIcon,
  ISOOverheadLoadIcon,
  ISOIonizingIcon,
  ISOSharpObjectIcon,
  ISOSlipperyIcon,
  ISOWarningIcon,
} from '@/components/icons/AnnotationsIcons'
import { AnnotationSize } from '@/services/hooks/template_attributes'

export type AnnotationsMenuItem = {
  id: string
  size?: AnnotationSize
  strokeColor?: string
  Icon: (props: IconProps) => JSX.Element
  description?: string
}

export type AnnotationsMenu = {
  menuItems: Array<AnnotationsMenuItem>
  TriggerIcon: (props: { className?: string }) => JSX.Element
  id: string
  columns: number
  colorOptionsEnabled?: boolean
  tooltipContent: string
  label: string
}

export const SHAPES_MENU_ITEMS: Array<AnnotationsMenuItem> = [
  {
    id: 'circle',
    size: 'small',
    Icon: CircleIcon,
  },
  {
    id: 'circle',
    size: 'medium',
    Icon: CircleIcon,
  },
  {
    id: 'circle',
    size: 'large',
    Icon: CircleIcon,
  },
  {
    id: 'triangle',
    size: 'small',
    Icon: TriangleIcon,
  },
  {
    id: 'triangle',
    size: 'medium',
    Icon: TriangleIcon,
  },
  {
    id: 'triangle',
    size: 'large',
    Icon: TriangleIcon,
  },
  {
    id: 'square',
    size: 'small',
    Icon: SquareIcon,
  },
  {
    id: 'square',
    size: 'medium',
    Icon: SquareIcon,
  },
  {
    id: 'square',
    size: 'large',
    Icon: SquareIcon,
  },
]

export const LINES_MENU_ITEMS: Array<AnnotationsMenuItem> = [
  {
    id: 'line',
    Icon: LineIcon,
  },
  {
    id: 'arrow',
    Icon: ArrowIcon,
  },
  {
    id: 'arrow-with-text--middle',
    Icon: ArrowWithMiddleTextIcon,
  },
  {
    id: 'arrow-with-text--bottom',
    Icon: ArrowWithBottomTextIcon,
  },
]

export const HARDWARE_SPECIFICS_MENU_ITEMS: Array<AnnotationsMenuItem> = [
  {
    id: 'torx',
    strokeColor: '#1E1E1E',
    size: 8,
    Icon: TorxIcon,
    description: 'Torx',
  },
  {
    id: 'phillips',
    strokeColor: '#1E1E1E',
    size: 8,
    Icon: PhillipsIcon,
    description: 'Phillips',
  },
  {
    id: 'hex',
    strokeColor: '#1E1E1E',
    size: 8,
    Icon: HexIcon,
    description: 'Hex',
  },
  {
    id: 'pozidriv',
    strokeColor: '#1E1E1E',
    size: 8,
    Icon: PozidrivIcon,
    description: 'Pozidriv',
  },
  {
    id: 'checkmark',
    strokeColor: '#008D5A',
    size: 10,
    Icon: CheckmarkIcon,
  },
  {
    id: 'x',
    strokeColor: '#E64343',
    size: 10,
    Icon: XIcon,
  },
  {
    id: 'fire_hazard',
    strokeColor: '#E64343',
    size: 10,
    Icon: FireHazardIcon,
  },
  {
    id: 'warning',
    strokeColor: '#D0CA36',
    size: 10,
    Icon: WarningIcon,
  },
  {
    description: 'W001-warning',
    id: 'iso_warning',
    size: 10,
    Icon: ISOWarningIcon,
  },
  {
    description: 'W002-explosive',
    id: 'iso_explosive',
    size: 10,
    Icon: ISOExplosiveIcon,
  },
  {
    description: 'W003-radioactive-ionizing-radiation',
    id: 'iso_ionizing',
    size: 10,
    Icon: ISOIonizingIcon,
  },
  {
    description: 'W005-non-ionizing-radiation',
    id: 'iso_non_ionizing',
    size: 10,
    Icon: ISONonIonizingIcon,
  },
  {
    description: 'W007-floor-level-obstacle',
    id: 'iso_obstacle',
    size: 10,
    Icon: ISOObstacleIcon,
  },
  {
    description: 'W011-slippy-surface',
    id: 'iso_slippery_surface',
    size: 10,
    Icon: ISOSlipperyIcon,
  },
  {
    description: 'W012-electricity',
    id: 'iso_electricity',
    size: 10,
    Icon: ISOElectricityIcon,
  },
  {
    description: 'W014-forklift',
    id: 'iso_forklift',
    size: 10,
    Icon: ISOForkliftIcon,
  },
  {
    description: 'W015-overhead-load',
    id: 'iso_overhead_load',
    size: 10,
    Icon: ISOOverheadLoadIcon,
  },
  {
    description: 'W021-flammable',
    id: 'iso_flammable',
    size: 10,
    Icon: ISOFlammableIcon,
  },
  {
    description: 'W022-sharp-object',
    id: 'iso_sharp_object',
    size: 10,
    Icon: ISOSharpObjectIcon,
  },
  {
    description: 'W087-loud-sounds',
    id: 'iso_loud_sounds',
    size: 10,
    Icon: ISOLoudSoundsIcon,
  },
]

export const SHAPES_MENU: AnnotationsMenu = {
  menuItems: SHAPES_MENU_ITEMS,
  TriggerIcon: ShapesIcon,
  id: 'shapes',
  columns: 3,
  colorOptionsEnabled: true,
  tooltipContent: 'Shapes',
  label: '',
}

export const LINES_MENU: AnnotationsMenu = {
  menuItems: LINES_MENU_ITEMS,
  TriggerIcon: LinesIcon,
  id: 'lines',
  columns: 4,
  colorOptionsEnabled: true,
  tooltipContent: 'Lines',
  label: '',
}

export const HARDWARE_SPECIFICS_MENU: AnnotationsMenu = {
  menuItems: HARDWARE_SPECIFICS_MENU_ITEMS,
  TriggerIcon: HardwareSpecificsIcon,
  id: 'specifics',
  columns: 4,
  tooltipContent: 'Hardware Specifics',
  label: '',
}

export const DEFAULT_ANNOTATIONS_MENUS: Array<AnnotationsMenu> = [
  SHAPES_MENU,
  LINES_MENU,
  HARDWARE_SPECIFICS_MENU,
]
export const ALL_ANNOTATIONS_ICONS = [
  ...SHAPES_MENU_ITEMS,
  ...HARDWARE_SPECIFICS_MENU_ITEMS,
  ...LINES_MENU_ITEMS,
]
